// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getWishLists } from "@insite/client-framework/Services/WishListService";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { WishListCollectionModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import useHandleApiError from "@store/Framework/Common/Hooks/useHandleApiError";
import {
    addProductToLists,
    getWishListIdsByProductIds,
    removeProductFromLists,
} from "@store/Framework/Services/BulkListService";
import { BunzlActions, useBunzlDispatch } from "@store/Framework/Store/Bunzl/BunzlReducer";
import { useToaster } from "@store/MobiusOverrides/Toast/ToasterContext";
import { toToolkitApplicationState } from "@toolkit/Framework/Store/ApplicationState";
import { Dispatch, useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

type ListType = "wishList" | "buyingList";
export interface WishListModelWithHasProduct extends WishListModel {
    hasProduct?: boolean;
}
const baseExpands = ["wishListLinecount"];

interface IdsBySelectedItems {
    idsWithProduct: string[];
    idsWithoutProduct: string[];
}
const getIdsBySelectedItems = (selectedItems: string[], lists: WishListModelWithHasProduct[]) => {
    const selectedWishListLines = selectedItems
        .map(id => lists?.find(list => list.id === id))
        .filter((list): list is WishListModelWithHasProduct => !!list);
    const idsBySelectedItems: IdsBySelectedItems = selectedWishListLines.reduce<IdsBySelectedItems>(
        (acc, list) => {
            if (list.hasProduct) {
                acc.idsWithProduct.push(list.id);
            } else {
                acc.idsWithoutProduct.push(list.id);
            }
            return acc;
        },
        { idsWithProduct: [], idsWithoutProduct: [] },
    );
    return idsBySelectedItems;
};

const getWishListIds = async (productId: string, dispatch: Dispatch<BunzlActions>) => {
    const response = await getWishListIdsByProductIds({ Products: [{ ProductId: productId }] });
    const wishListIds = response.productWishListIds[0]?.wishListIds || [];
    dispatch({ type: "Store/Pages/bulkLists/setWishListIdsWithProduct", wishListIdsWithProduct: wishListIds });
};

const useBulkLists = (listType: ListType | undefined = undefined) => {
    const dispatch = useDispatch();
    const {
        bulkLists: lists,
        isLoading,
        selectedProductId,
        selectedListIds = [],
        filterBy,
        showSelected,
        filteredLists,
    } = useAppSelector(state => toToolkitApplicationState(state).bunzl.pages.bulkLists);
    const toaster = useToaster();
    const handleApiError = useHandleApiError();
    const filteredListIds = filteredLists?.map(list => list.id) || [];
    const filteredSelectedItemIds = selectedListIds.filter(id => filteredListIds.includes(id));
    const idsBySelectedItems = getIdsBySelectedItems(filteredSelectedItemIds, filteredLists || []);
    const selectorProductId = useAppSelector(getProductSelector)?.selectedProductInfo?.productId;
    const { productNumber, productTitle } = useAppSelector(
        state => state.data.products.byId?.[selectorProductId || ""] ?? {},
    );
    const productName = `${productTitle ?? ""} (${productNumber ?? ""})`;
    const toggleAllItems = (toggleIds: string[]) => {
        const isAllSelected = toggleIds.every(id => selectedListIds.includes(id));
        const newSelectedItems = isAllSelected
            ? selectedListIds.filter(id => !toggleIds.includes(id))
            : [...new Set([...selectedListIds, ...toggleIds])];

        bunzlDispatch({ type: "Store/Pages/bulkLists/setSelectedListIds", selectedListIds: newSelectedItems });
        return newSelectedItems;
    };

    const toggleItem = (toggleId: string) => {
        const newItems = selectedListIds.includes(toggleId)
            ? selectedListIds.filter(id => id !== toggleId)
            : [...selectedListIds, toggleId];

        bunzlDispatch({ type: "Store/Pages/bulkLists/setSelectedListIds", selectedListIds: newItems });
        return newItems;
    };

    const bunzlDispatch = useBunzlDispatch();
    const setIsLoading = useCallback(
        (isLoading: boolean) => bunzlDispatch({ type: "Store/Pages/bulkLists/setIsLoading", isLoading }),
        [bunzlDispatch],
    );
    const hasLoadedLists = useRef(false);
    const handleToggleAllItems = (ids: string[]) => {
        const newItems = toggleAllItems(ids);
        bunzlDispatch({ type: "Store/Pages/bulkLists/setSelectedListIds", selectedListIds: newItems });
    };
    const handleToggleItem = (id: string) => {
        const newItems = toggleItem(id);
        bunzlDispatch({ type: "Store/Pages/bulkLists/setSelectedListIds", selectedListIds: newItems });
    };
    const handleAddProductToLists = () =>
        handleApiError(async () => {
            const productId = selectorProductId;
            if (productId) {
                setIsLoading(true);
                await addProductToLists(productId, idsBySelectedItems.idsWithoutProduct);
                // We need to reload the lists to get the updated hasProduct property.
                bunzlDispatch({
                    type: "Store/Pages/bulkLists/loadBulkList",
                    bulkLists: undefined,
                });
                setIsLoading(false);
                toaster.addToast({ body: `${productName} added to lists`, messageType: "success" });
            }
        });

    const handleDeSelectAllItems = () => {
        bunzlDispatch({ type: "Store/Pages/bulkLists/setSelectedListIds", selectedListIds: [] });
    };

    const handleRemoveProductFromLists = () =>
        handleApiError(async () => {
            const productId = selectorProductId;
            if (productId) {
                setIsLoading(true);
                await removeProductFromLists(productId, idsBySelectedItems.idsWithProduct);
                bunzlDispatch({
                    type: "Store/Pages/bulkLists/loadBulkList",
                    bulkLists: undefined,
                });

                setIsLoading(false);
                toaster.addToast({ body: `${productName} removed from lists`, messageType: "success" });
            }
        });
    const handleToggleShowSelected = () => {
        bunzlDispatch({ type: "Store/Pages/bulkLists/setShowSelected", showSelected: !showSelected });
    };
    // We don't want to have effects fire if there is no list type as only the table should be listening to updates.
    if (selectorProductId !== selectedProductId) {
        bunzlDispatch({ type: "Store/Pages/bulkLists/setSelectedProductId", selectedProductId: selectorProductId });
        if (selectorProductId) {
            getWishListIds(selectorProductId, bunzlDispatch);
        } else {
            bunzlDispatch({
                type: "Store/Pages/bulkLists/setWishListIdsWithProduct",
                wishListIdsWithProduct: undefined,
            });
        }
    }
    const getLists = useCallback(async () => {
        if (!listType) {
            return;
        }
        const additionalExpands = listType === "buyingList" ? [...baseExpands, "buyinglists"] : baseExpands;

        const response = await handleApiError<WishListCollectionModel>(() =>
            getWishLists({ additionalExpands, pageSize: 1000 }),
        );

        // Reset the wishlists as the wishlist reducer will throw an error if the lists are not reset.
        dispatch({ type: "Data/WishLists/Reset" });

        const editableWishLists = response?.wishListCollection?.filter(list => list.allowEdit || !list.isSharedList);
        if (editableWishLists) {
            bunzlDispatch({ type: "Store/Pages/bulkLists/loadBulkList", bulkLists: editableWishLists });
            // we need to load wishlists into optimizely's state to get accurate share information.
            dispatch({ type: "Data/WishLists/CompleteLoadWishLists", result: response, parameter: {} });
        }
        if (selectedProductId) {
            getWishListIds(selectedProductId, bunzlDispatch);
        }
    }, [bunzlDispatch, dispatch, handleApiError, listType, selectedProductId]);

    useEffect(() => {
        return () => {
            bunzlDispatch({ type: "Store/Pages/bulkLists/setSelectedProductId", selectedProductId: undefined });
            bunzlDispatch({
                type: "Store/Pages/bulkLists/setWishListIdsWithProduct",
                wishListIdsWithProduct: undefined,
            });
            bunzlDispatch({ type: "Store/Pages/bulkLists/loadBulkList", bulkLists: undefined });
            bunzlDispatch({ type: "Store/Pages/bulkLists/setSelectedListIds", selectedListIds: [] });
            setIsLoading(false);
        };
    }, [bunzlDispatch, setIsLoading]);

    useEffect(() => {
        if (!listType) {
            return;
        }
        const getListEffect = async () => {
            setIsLoading(true);
            await getLists();
            setIsLoading(false);
            hasLoadedLists.current = false;
        };
        if (!lists && isLoading === false && !hasLoadedLists.current) {
            hasLoadedLists.current = true;
            getListEffect();
        }
    }, [bunzlDispatch, getLists, isLoading, listType, lists, setIsLoading]);

    return {
        lists: filteredLists,
        selectedListIds: filteredSelectedItemIds,
        isLoading,
        toggleItem: handleToggleItem,
        toggleAllItems: handleToggleAllItems,
        selectorProductId,
        idsBySelectedItems,
        filterBy,
        handleAddProductToLists,
        handleRemoveProductFromLists,
        toggleAllFilteredItems: () => handleToggleAllItems(filteredListIds),
        handleDeSelectAllItems,
        filteredListIds,
        handleToggleShowSelected,
        showSelected,
        productName,
    };
};

export default useBulkLists;
